import type React from 'react';

import { Stack, useTheme } from '@mui/material';
import { OrdReferenceInput, OrdFormList, OrdIconStandard } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { useGetRecordRepresentation, useRecordContext } from 'react-admin';

import { getColor } from 'src/lib/colorUtils';

import { CalendarColor } from '../CalendarColor';

import type { ResourceType } from '../../types';
import type { Calendar } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface CalendarReferenceInputProps extends ResourceType {
  isCreate: boolean;
  parentId: string | undefined;
  setParentId: (pid: string) => void;
  selectedCalendarIds: Identifier[];
}

export const CalendarReferenceInput = ({
  isCreate,
  isPersonalResource,
  parentId,
  setParentId,
  selectedCalendarIds,
}: CalendarReferenceInputProps): React.ReactElement => {
  const calendarResource = isPersonalResource ? `me/${Resources.calendars}` : Resources.calendars;

  const calendar = isCreate ? (parentId ?? (selectedCalendarIds?.[0] as string)) : parentId;

  const calendarColor = getColor(calendar ?? '');
  const getRecordRepresentation = useGetRecordRepresentation(calendarResource);

  const OptionRenderer = (): React.ReactElement => {
    const record = useRecordContext();
    const theme = useTheme();
    return (
      <Stack direction='row' alignItems='center'>
        <CalendarColor size='12px' sx={{ marginRight: theme.spacing(2) }} />
        {getRecordRepresentation(record)}
      </Stack>
    );
  };

  const optionText = <OptionRenderer />;
  const matchSuggestion = (filter: string, choice: Calendar): boolean => {
    return (
      choice.title.toLowerCase().includes(filter.toLowerCase()) ||
      choice.title.toLowerCase().includes(filter.toLowerCase())
    );
  };

  return (
    <OrdFormList icon={<OrdIconStandard name='calendars' />}>
      <OrdReferenceInput
        resource={calendarResource}
        source={'calendar_id'}
        onChange={(value) => {
          setParentId(value as string);
        }}
        defaultValue={calendar}
        disabled={!isCreate}
        optionText={optionText}
        inputText={(el: Calendar) => el.title}
        matchSuggestion={matchSuggestion}
        alwaysOn
        sx={
          isCreate && calendar !== undefined
            ? {
                '& .MuiOutlinedInput-notchedOutline': { border: `2px solid ${calendarColor}` },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: `2px solid ${calendarColor}` },
              }
            : {}
        }
        isRequired
      />
    </OrdFormList>
  );
};
